(() => {
  if (customElements.get('shipping-protection')) return;

  const widgets = new Set();
  const checkoutSelector = '[name="checkout"], #CartDrawer-Checkout, #checkout, [data-protection-checkout], a[href*="/checkout"]';
  const acceleratedCheckoutSelector = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons';
  const preferenceKey = 'theme:shipping-protection-choice';
  const optOutAttribute = '_shipping_protection_opt_out';
  const state = { pending: false, uncertain: false, cart: null, revision: 0, intent: null, autoVerified: false, autoFailure: null, lastConfig: null, resetFailed: false, preference: loadPreference(), checkoutControls: new Map(), checkoutContainers: new Map() };

  function loadPreference() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try {
        const stored = JSON.parse(window[name]?.getItem(preferenceKey) || 'null');
        if (stored && typeof stored.optedOut === 'boolean') return stored;
      } catch (error) { /* The server cart attribute remains available if browser storage is blocked. */ }
    }
    return { token: '', optedOut: false, reset: false };
  }

  function savePreference() {
    for (const name of ['localStorage', 'sessionStorage']) {
      try { window[name]?.setItem(preferenceKey, JSON.stringify(state.preference)); } catch (error) { /* Server-backed preference is the fallback. */ }
    }
  }

  function cartToken(cart) {
    return typeof cart?.token === 'string' ? cart.token.split('?')[0] : '';
  }

  function refreshPreference(cart) {
    const token = cartToken(cart);
    if (token && state.preference.token && token !== state.preference.token) {
      state.preference = { token, optedOut: false, reset: true };
      state.autoFailure = null;
      state.resetFailed = false;
    } else if (token) state.preference.token = token;
    if (!hasMerchandise(cart, state.lastConfig || {})) {
      state.preference.optedOut = false;
      state.preference.reset = String(cart.attributes?.[optOutAttribute]) === 'true';
      state.autoFailure = null;
    } else if (String(cart.attributes?.[optOutAttribute]) === 'true' && !state.preference.reset) {
      state.preference.optedOut = true;
    } else if (cart.attributes && String(cart.attributes[optOutAttribute]) !== 'true' && !state.preference.optedOut) {
      state.preference.reset = false;
    }
    savePreference();
  }

  function rememberDecision(wanted) {
    state.preference.optedOut = !wanted;
    state.preference.reset = wanted;
    state.autoFailure = null;
    savePreference();
  }

  function preferenceAttributes(cart, config) {
    const current = String(cart.attributes?.[optOutAttribute] || '');
    const desired = hasMerchandise(cart, config) && state.preference.optedOut ? 'true' : '';
    return current === desired ? null : { [optOutAttribute]: desired };
  }

  function automaticEligible(widget) {
    return widget.dataset.autoEnable === 'true' && widget.dataset.enabled === 'true' && widget.dataset.preview !== 'true' && isConfigured(settings(widget));
  }

  function automaticKey(widget) {
    const config = settings(widget);
    return `${state.preference.token}:${config.productId}:${config.variantId}`;
  }

  function needsAutomatic(widget, cart) {
    if (!automaticEligible(widget) || !validCart(cart) || !hasMerchandise(cart, settings(widget))) return false;
    return !state.autoVerified || (!state.preference.optedOut && !protectionLines(cart, settings(widget)).length && state.autoFailure !== automaticKey(widget));
  }

  function route(name, fallback) {
    const root = window.Shopify?.routes?.root || '/';
    const value = window.routes?.[name] || `${root.replace(/\/?$/, '/')}${fallback}`;
    return value.replace(/\/$/, '').replace(/\.js$/, '') + '.js';
  }

  function settings(widget) {
    return {
      variantId: String(widget.dataset.variantId || ''),
      productId: String(widget.dataset.productId || ''),
      error: widget.dataset.errorMessage || 'Impossible de mettre à jour la protection. Réessayez.',
      retry: widget.dataset.retryMessage || 'Vérifiez votre connexion puis réessayez.'
    };
  }

  function isConfigured(config) {
    return /^[1-9]\d*$/.test(config.variantId) && /^[1-9]\d*$/.test(config.productId);
  }

  function validCart(cart) {
    return cart && Array.isArray(cart.items) && Number.isFinite(cart.item_count) && cart.item_count >= 0;
  }

  function acceptCart(cart, verified = false) {
    state.cart = cart;
    state.revision += 1;
    if (verified || cartToken(cart)) state.autoVerified = true;
    refreshPreference(cart);
  }

  function isProtection(item, config) {
    return String(item.properties?._shipping_protection) === 'true' ||
      (config.productId && String(item.product_id || '') === config.productId) ||
      (config.variantId && String(item.variant_id || item.id || '') === config.variantId);
  }

  function protectionLines(cart, config) {
    return cart.items.filter((item) => isProtection(item, config));
  }

  function hasMerchandise(cart, config) {
    return cart.items.some((item) => !isProtection(item, config) && Number(item.quantity) > 0);
  }

  function requiresCleanup(widget, cart) {
    if (widget.dataset.preview === 'true' || !validCart(cart)) return false;
    const config = settings(widget);
    const lines = protectionLines(cart, config);
    const obsolete = lines.some((item) => String(item.properties?._shipping_protection) === 'true' && String(item.product_id || '') !== config.productId);
    return lines.length > 0 && (widget.dataset.enabled === 'false' || state.preference.optedOut || obsolete || !hasMerchandise(cart, config) || !matchesSelection(cart, true, config));
  }

  function matchesSelection(cart, wanted, config) {
    const lines = protectionLines(cart, config);
    if (!wanted) return lines.length === 0;
    return lines.length === 1 && Number(lines[0].quantity) === 1 && String(lines[0].variant_id || lines[0].id) === config.variantId;
  }

  function sectionIds() {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer?.getSectionsToRender?.() || [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
    return [...new Set(sections.map((section) => section.section || section.id).filter(Boolean))];
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: { Accept: 'application/json', ...options.headers }
    });
    const data = await response.json();
    if (!response.ok || data?.errors || (Number(data?.status) >= 400)) throw new Error('Cart request failed');
    return data;
  }

  async function readCart() {
    const cart = await request(route('cart_url', 'cart'), { cache: 'no-store' });
    if (!validCart(cart)) throw new Error('Cart state unavailable');
    return cart;
  }

  function mutate(name, fallback, payload) {
    return request(route(name, fallback), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, sections: sectionIds(), sections_url: window.location.pathname })
    });
  }

  async function applySelection(cart, wanted, config, allowAdd = true) {
    const lines = protectionLines(cart, config);
    const attributes = preferenceAttributes(cart, config);
    if (matchesSelection(cart, wanted, config) && !attributes) return null;

    let response = null;
    const selected = wanted && lines.find((item) => String(item.variant_id || item.id) === config.variantId);
    if (lines.length || attributes) {
      const updates = {};
      lines.forEach((item) => { updates[item.key] = item === selected ? 1 : 0; });
      response = await mutate('cart_update_url', 'cart/update', { updates, ...(attributes ? { attributes } : {}) });
    }

    if (wanted && !selected && allowAdd) {
      // Recheck after removing an old service variant: merchandise may have changed meanwhile.
      const current = lines.length || attributes ? await readCart() : cart;
      if (!hasMerchandise(current, config)) return response;
      if (protectionLines(current, config).length) return response;
      response = await mutate('cart_add_url', 'cart/add', {
        items: [{ id: Number(config.variantId), quantity: 1, properties: { _shipping_protection: 'true' } }]
      });
    }
    return response;
  }

  async function freshSections() {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionIds().join(','));
    return { sections: await request(url.pathname + url.search, { cache: 'no-store', headers: { Accept: 'text/html' } }) };
  }

  async function renderCart(response) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.renderContents) return;
    const complete = (result) => sectionIds().every((id) => typeof result?.sections?.[id] === 'string');
    if (!complete(response)) response = await freshSections();
    if (!complete(response)) throw new Error('Cart sections unavailable');
    const body = drawer.querySelector('.cart-drawer__body');
    if (body) drawer.cartBodyScroll = body.scrollTop;
    const terms = [...drawer.querySelectorAll('tnc-chekcbox')].map((checkbox) => ({
      id: checkbox.id,
      checked: checkbox.checked === true,
      warningHidden: checkbox.warningTextElement?.classList.contains('hidden')
    }));
    // The native true flag preserves the current open/closed state of the drawer.
    drawer.renderContents(response, true);
    drawer.querySelectorAll('tnc-chekcbox').forEach((checkbox) => {
      const before = terms.find((entry) => entry.id === checkbox.id);
      if (!before) return;
      checkbox.checked = before.checked;
      checkbox.dataset.checked = String(before.checked);
      if (checkbox.disableButton && checkbox.checkoutButton) checkbox.checkoutButton.classList.toggle('disabled', !before.checked);
      if (checkbox.warningTextElement && before.warningHidden !== undefined) checkbox.warningTextElement.classList.toggle('hidden', before.warningHidden);
    });
  }

  function showError(config, message) {
    widgets.forEach((widget) => {
      if (settings(widget).productId !== config.productId) return;
      const error = widget.querySelector('[data-protection-error]');
      if (error) { error.textContent = message; error.hidden = !message; }
      const retry = widget.querySelector('[data-protection-retry]');
      if (retry) retry.hidden = !message;
      if (message) widget.hidden = false;
    });
  }

  function syncWidgets() {
    widgets.forEach((widget) => {
      const config = settings(widget);
      const toggle = widget.querySelector('[data-protection-toggle]');
      if (toggle && validCart(state.cart) && !state.uncertain) toggle.checked = protectionLines(state.cart, config).length > 0;
    });
  }

  function lockCheckoutControls() {
    document.querySelectorAll(checkoutSelector).forEach((control) => {
      if (state.checkoutControls.has(control)) return;
      state.checkoutControls.set(control, { disabled: control.disabled, ariaDisabled: control.getAttribute('aria-disabled') });
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
    });
    document.querySelectorAll(acceleratedCheckoutSelector).forEach((container) => {
      if (state.checkoutContainers.has(container)) return;
      state.checkoutContainers.set(container, { inert: container.inert, inertAttribute: container.getAttribute('inert') });
      // Inert also blocks accelerated payment controls rendered inside iframes or shadow roots.
      container.setAttribute('inert', '');
      if ('inert' in container) container.inert = true;
    });
  }

  function checkoutBlocked() {
    return state.pending || (state.uncertain && widgets.size > 0) ||
      [...widgets].some((widget) => requiresCleanup(widget, state.cart) || needsAutomatic(widget, state.cart));
  }

  function syncCheckoutControls() {
    if (checkoutBlocked()) lockCheckoutControls();
    else {
      state.checkoutControls.forEach((before, control) => {
        if (!control.isConnected) return;
        if ('disabled' in control) control.disabled = before.disabled;
        if (before.ariaDisabled === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', before.ariaDisabled);
      });
      state.checkoutControls.clear();
      state.checkoutContainers.forEach((before, container) => {
        if (!container.isConnected) return;
        if (before.inertAttribute === null) container.removeAttribute('inert');
        else container.setAttribute('inert', before.inertAttribute);
        if (typeof before.inert === 'boolean') container.inert = before.inert;
      });
      state.checkoutContainers.clear();
    }
  }

  function setPending(pending) {
    state.pending = pending;
    widgets.forEach((widget) => widget.setBusy(pending));
    syncCheckoutControls();
  }

  function notifyCart(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Native cart-items already rendered above; other subscribers still receive authoritative data.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', shippingProtection: true, cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('cartQuantityUpdated', { detail: { shippingProtection: true } }));
  }

  async function changeProtection(widget, wanted, orphan = false, automatic = false) {
    const config = settings(widget);
    if (state.pending || widget.dataset.preview === 'true' || (wanted && (!isConfigured(config) || widget.dataset.enabled === 'false'))) { syncWidgets(); return; }
    state.revision += 1;
    if (!orphan && !automatic) rememberDecision(wanted);
    widget.retryWanted = wanted;
    state.intent = { productId: config.productId, wanted, cleanup: orphan, automatic };
    const restoreFocus = document.activeElement === widget.querySelector('[data-protection-toggle]');
    setPending(true);
    showError(config, '');
    let recovered = false;
    try {
      let cart = await readCart();
      acceptCart(cart, true);
      state.uncertain = false;
      if (!orphan && !automatic) rememberDecision(wanted);
      if (automatic && (state.preference.optedOut || ![...widgets].some((current) => settings(current).productId === config.productId && automaticEligible(current)))) wanted = false;
      if (!hasMerchandise(cart, config)) wanted = false;
      if (orphan && wanted && !protectionLines(cart, config).some((item) => String(item.variant_id || item.id) === config.variantId)) wanted = false;
      state.intent = { productId: config.productId, wanted, cleanup: orphan, automatic };
      await applySelection(cart, wanted, config, !orphan);
      cart = await readCart();
      // A concurrent removal can leave the service alone; never sell it as the only cart item.
      if (!hasMerchandise(cart, config) && protectionLines(cart, config).length) {
        wanted = false;
        await applySelection(cart, false, config);
        cart = await readCart();
      }
      acceptCart(cart, true);
      // Bundled mutation HTML can precede a concurrent merchandise update. Refresh before rendering.
      await renderCart(null);
      if (!matchesSelection(cart, wanted, config)) throw new Error('Protection state changed');
      recovered = true;
    } catch (error) {
      // A failed response can follow a committed mutation. Read the server before permitting a retry.
      let cartConfirmed = false;
      try {
        acceptCart(await readCart(), true);
        cartConfirmed = true;
        state.uncertain = false;
        await renderCart(null);
        recovered = matchesSelection(state.cart, wanted, config);
      } catch (refreshError) {
        // Only an unavailable cart state blocks checkout; a section-render error alone cannot add a fee.
        state.uncertain = !cartConfirmed;
      }
      if (!recovered) showError(config, state.uncertain ? config.retry : config.error);
    } finally {
      if (recovered) state.autoFailure = null;
      else if (automatic || wanted) state.autoFailure = automaticKey(widget);
      syncWidgets();
      setPending(false);
      if (recovered && validCart(state.cart)) notifyCart(state.cart);
      if (restoreFocus && document.querySelector('cart-drawer')?.classList.contains('active')) {
        [...widgets].find((current) => settings(current).productId === config.productId)?.querySelector('[data-protection-toggle]')?.focus({ preventScroll: true });
      }
      if (recovered && !orphan) reconcileProtection();
    }
  }

  function removeOrphanIfNeeded() {
    if (state.pending || state.uncertain || !validCart(state.cart)) return;
    const widget = [...widgets].find((current) => {
      return requiresCleanup(current, state.cart);
    });
    if (widget) {
      const config = settings(widget);
      const lines = protectionLines(state.cart, config);
      const current = lines.some((item) => String(item.variant_id || item.id) === config.variantId);
      const obsolete = lines.some((item) => String(item.properties?._shipping_protection) === 'true' && String(item.product_id || '') !== config.productId);
      // Only reduce an already-selected fee; automatic cleanup never adds a replacement service.
      const keepOne = widget.dataset.enabled !== 'false' && !state.preference.optedOut && isConfigured(config) && hasMerchandise(state.cart, config) && !obsolete && current;
      changeProtection(widget, keepOne, true);
    }
  }

  function reconcileProtection() {
    if (state.pending || state.uncertain || !validCart(state.cart)) return;
    if ([...widgets].some((widget) => requiresCleanup(widget, state.cart))) {
      removeOrphanIfNeeded();
      return;
    }
    const widget = [...widgets].find((current) => needsAutomatic(current, state.cart));
    if (widget) changeProtection(widget, true, false, true);
  }

  async function resetEmptyPreference(cart) {
    if (hasMerchandise(cart, state.lastConfig || {}) || String(cart.attributes?.[optOutAttribute]) !== 'true' || state.resetFailed || state.pending) return;
    state.revision += 1;
    setPending(true);
    try {
      await mutate('cart_update_url', 'cart/update', { attributes: { [optOutAttribute]: '' } });
      state.resetFailed = false;
    } catch (error) {
      state.resetFailed = true;
    } finally {
      try { acceptCart(await readCart(), true); state.uncertain = false; } catch (error) {
        state.uncertain = true;
        widgets.forEach((widget) => showError(settings(widget), settings(widget).retry));
      }
      syncWidgets();
      setPending(false);
      reconcileProtection();
    }
  }

  async function handleCartUpdate(event) {
    if (state.pending) return;
    const revision = ++state.revision;
    let cart = event?.cartData;
    if (!validCart(cart) && document.querySelector('cart-drawer')?.classList.contains('is-empty')) {
      // Native clearCart removes the widget before publishing; preserve that observed empty boundary.
      state.preference.optedOut = false;
      state.preference.reset = true;
      state.autoFailure = null;
      state.autoVerified = false;
      savePreference();
    }
    if (!validCart(cart)) {
      // ProductForm add.js and clearCart events do not contain an authoritative full cart.
      try { cart = await readCart(); } catch (error) { return; }
    }
    if (state.pending || revision !== state.revision) return;
    acceptCart(cart, true);
    state.uncertain = false;
    syncWidgets();
    syncCheckoutControls();
    if (!hasMerchandise(cart, state.lastConfig || {})) {
      await resetEmptyPreference(cart);
      reconcileProtection();
      return;
    }
    reconcileProtection();
  }

  function guardCheckout(event) {
    const invalid = [...widgets].some((widget) => requiresCleanup(widget, state.cart));
    if (!checkoutBlocked()) return;
    const isCheckout = event.type === 'submit'
      ? event.submitter?.name === 'checkout' || event.target.querySelector?.('[name="checkout"]') || /\/checkout(?:[/?]|$)/.test(event.target.action || '')
      : event.target.closest?.(checkoutSelector + ', ' + acceleratedCheckoutSelector);
    if (!isCheckout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (state.uncertain) widgets.forEach((widget) => showError(settings(widget), settings(widget).retry));
    else if (invalid && !state.pending) widgets.forEach((widget) => {
      if (requiresCleanup(widget, state.cart)) showError(settings(widget), settings(widget).error);
    });
  }

  document.addEventListener('click', guardCheckout, true);
  document.addEventListener('submit', guardCheckout, true);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);

  class ShippingProtection extends HTMLElement {
    connectedCallback() {
      const config = settings(this);
      if (this.dataset.preview === 'true') return;
      if (!isConfigured(config) && this.dataset.enabled !== 'false') return;
      widgets.add(this);
      state.lastConfig = config;
      const embedded = this.querySelector('[data-protection-cart]');
      if (!state.pending && embedded) {
        try {
          const cart = JSON.parse(embedded.textContent);
          if (validCart(cart)) { acceptCart(cart); state.uncertain = false; }
        } catch (error) { /* A fresh server read is required before a customer can change a fee. */ }
      }
      this.handleChange = (event) => {
        if (!event.target.matches('[data-protection-toggle]')) return;
        if (state.pending) { syncWidgets(); return; }
        changeProtection(this, event.target.checked);
      };
      this.addEventListener('change', this.handleChange);
      this.handleRetry = (event) => {
        if (!event.target.closest('[data-protection-retry]')) return;
        event.preventDefault();
        const wanted = state.intent?.productId === settings(this).productId ? state.intent.wanted : (this.retryWanted ?? this.querySelector('[data-protection-toggle]')?.checked ?? false);
        const cleanup = state.intent?.productId === settings(this).productId && state.intent.cleanup === true;
        const automatic = state.intent?.productId === settings(this).productId && state.intent.automatic === true;
        changeProtection(this, this.dataset.enabled === 'false' ? false : wanted, cleanup, automatic);
      };
      this.addEventListener('click', this.handleRetry);
      this.setBusy(state.pending);
      syncCheckoutControls();
      syncWidgets();
      queueMicrotask(reconcileProtection);
    }

    disconnectedCallback() {
      widgets.delete(this);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleRetry);
    }

    setBusy(busy) {
      this.classList.toggle('is-loading', busy);
      this.setAttribute('aria-busy', String(busy));
      const toggle = this.querySelector('[data-protection-toggle]');
      if (!toggle) return;
      if (busy && this.wasDisabled === undefined) this.wasDisabled = toggle.disabled;
      if (busy) toggle.disabled = true;
      else if (this.wasDisabled !== undefined) { toggle.disabled = this.wasDisabled; this.wasDisabled = undefined; }
    }
  }

  customElements.define('shipping-protection', ShippingProtection);
  // A page loaded with an already-empty cart has no widget to clear an earlier refusal.
  if (state.preference.optedOut || state.preference.reset || document.querySelector('cart-drawer')?.classList.contains('is-empty')) queueMicrotask(() => handleCartUpdate({}));
})();
